import { useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { NvErrorState, NvLoginPrompt, NvRankRow, nvAgentDetailPath, nvLoginPath } from "@/components/navigator";
import { useAuth } from "@/contexts/auth-context";
import { useRequest } from "@/hooks/use-request";
import { agentDisplayName } from "@/lib/agent-display-name";
import { cn } from "@/lib/utils";
import { getTrending } from "@/services/navigator/finder-service";

// HOME · Agent Finder 공용 조각: △ Blur 영역(Figma LAYER_BLUR 10 → CSS 5px), 주목받는 에이전트 패널.

// 로그인 뒤 지금 보던 화면으로 돌아오는 로그인 이동 (로그인 유도 카드의 [로그인])
export function useLoginRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  return () => navigate(nvLoginPath(location.pathname + location.search));
}

export interface LockedPrompt {
  text: ReactNode;
  // "center" = 흐린 영역 한가운데, 숫자 = 영역 위에서 그만큼 아래(px)
  position?: "center" | number;
  onLogin: () => void;
}

// 잠긴 영역: 흐리게 그리고, 누르면 이용 권한 안내 (slide 28). prompt 가 있으면 흐린 영역 위에 로그인 유도 카드를 한 번 얹어요(10/13 제안 21).
// 로그인 유도는 비회원에게만 — 이미 로그인한 공급자 · 승인 대기에게 "로그인하면"은 맞지 않아서 호출하는 쪽이 비회원일 때만 prompt 를 넘겨요(그 문구는 문서에 없어요).
export function LockedArea({
  locked,
  onBlocked,
  children,
  className,
  prompt,
}: {
  locked: boolean;
  onBlocked: () => void;
  children: ReactNode;
  className?: string;
  prompt?: LockedPrompt;
}) {
  if (!locked) return <div className={className}>{children}</div>;
  const position = prompt?.position ?? "center";
  return (
    <div className={cn("relative", className)}>
      <div aria-hidden className="pointer-events-none select-none blur-[5px]">
        {children}
      </div>
      <button type="button" onClick={onBlocked} aria-label="이용 권한 안내 보기" className="absolute inset-0 z-20 cursor-pointer" />
      {prompt && (
        <div
          className={cn("pointer-events-none absolute inset-x-0 z-30 flex justify-center", position === "center" ? "inset-y-0 items-center" : "items-start")}
          style={position === "center" ? undefined : { top: position }}
        >
          <NvLoginPrompt onLogin={prompt.onLogin} className="pointer-events-auto">
            {prompt.text}
          </NvLoginPrompt>
        </div>
      )}
    </div>
  );
}

// ── 주목받는 에이전트 (HOME과 같은 순위 데이터, IA 69 · 70) ──────────────

const LOCKED_RANK_SUMMARY = "로그인 후 확인할 수 있어요";

// size: finder = Agent Finder 오른쪽 380 패널(제목 16), home = HOME 588 카드(제목 18, Figma 203:13668)
export function TrendingPanel({ onBlocked, size = "finder", className }: { onBlocked: () => void; size?: "finder" | "home"; className?: string }) {
  const { accessLevel, isLoading } = useAuth();
  const goLogin = useLoginRedirect();
  const [period, setPeriod] = useState<"weekly" | "monthly">("weekly");
  // The ranking depends on the viewer's access level on the server (PwC-internal viewers get a different one, guests a locked one):
  // load once the session check has finished and again when the level changes (login · logout), like the HOME widgets.
  // The previous list stays while the next one loads (no flash); a late response of the other period / level is ignored
  const { data, error, reload } = useRequest((signal) => getTrending(period, signal), [period, accessLevel], { enabled: !isLoading, keepPreviousData: true });
  const locked = data?.locked ?? false;
  return (
    <aside
      aria-label="주목받는 에이전트"
      className={cn("flex flex-col rounded-nv-card border border-nv-border bg-nv-surface pb-4 pt-[22px]", size === "home" ? "px-6" : "px-[22px]", className)}
    >
      <div className="flex items-start justify-between pb-2">
        <h2 className={cn("text-nv-text-primary", size === "home" ? "pt-[3px] text-[18px] font-medium leading-[22px]" : "pt-1 text-[16px] font-medium leading-[19px]")}>
          주목받는 에이전트
        </h2>
        <div role="tablist" aria-label="기간" className="flex gap-[2px] rounded-nv-pill bg-nv-bg-page p-[3px]">
          {(["weekly", "monthly"] as const).map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={period === p}
              onClick={() => (locked ? onBlocked() : setPeriod(p))}
              className={cn(
                "h-[21px] rounded-nv-pill px-[10px] text-[11px] leading-[13px]",
                period === p ? "bg-nv-surface font-bold text-nv-text-primary" : "font-medium text-nv-text-secondary",
              )}
            >
              {p === "weekly" ? "주간" : "월간"}
            </button>
          ))}
        </div>
      </div>
      {error ? (
        <NvErrorState variant="plain" error={error} onRetry={reload} />
      ) : (
        <LockedArea locked={locked} onBlocked={onBlocked} prompt={accessLevel === "guest" ? { text: "로그인하면 순위를 볼 수 있어요", onLogin: goLogin } : undefined}>
          {data?.items.map((item, i) => (
            <NvRankRow
              key={item.id}
              rank={item.rank}
              name={agentDisplayName(item.name)}
              summary={item.summary ?? LOCKED_RANK_SUMMARY}
              change={item.change}
              to={locked ? undefined : nvAgentDetailPath(item.id)}
              linkState={{ source: "finder" }}
              className={cn("py-3", i === data.items.length - 1 && "border-b-0")}
            />
          ))}
        </LockedArea>
      )}
    </aside>
  );
}
